/**
 * Copyright (c) 2025 Tj L Collins, DaddySavesTheWorld, and Mythbound Adventure Studio
 * All Rights Reserved
 *
 * This file is part of Mythbound Adventure Studio, a proprietary software project.
 * Unauthorized copying, modification, distribution, or use of this software,
 * via any medium, is strictly prohibited without explicit written permission
 * from the copyright holders.
 *
 * For licensing inquiries, contact: taleweavertik@gmail.com
 * 
 * @fileoverview Audio capture and processing for voice bridge
 * @version 2.1.0 - Added test utilities for round-trip verification (PRD 5)
 * @since v0.0.07
 * 
 * Pipeline: Browser Mic → AudioWorklet → PCM frames → Protobuf encode → WebSocket binary
 * 
 * Enhanced: December 16, 2025 (Agent A - Voice Pipeline)
 * - PRD 5: Added sendTestTone() for integration testing
 * - Added generateSineWave() helper for test audio generation
 * - Used by !voice_e2e_test command for round-trip verification
 * 
 * Dependencies:
 * - websocketService for binary transport
 * - protobuf messages (mythbound.websocket.v1.UniversalMessage)
 * - audioSettings for configuration constants
 */

import { errorManager, ErrorSeverity } from '../services/error/ErrorManager';
import { webSocketService } from '../services/websocket/websocketService';
import { mythbound } from '../generated/messages';
import { VOICE_BRIDGE_CONFIG, detectVoiceCapabilities } from '../config/audioSettings';

/**
 * Audio processing standardization for voice bridge
 *
 * CRITICAL: These parameters MUST match the Discord bot implementation
 * and backend audio pipeline contract (shared/contracts/voice/v1/audio-pipeline.yaml)
 * to ensure consistent processing across platforms.
 * 
 * Transport: WebSocket binary (protobuf) for bandwidth efficiency (73% reduction vs JSON)
 * Format: 48kHz PCM mono, 16-bit, sent as protobuf VoiceDataMessage wrapped in UniversalMessage
 */
export class AudioProcessor {
  // Constants that MUST match server implementation (from audio-pipeline.yaml)
  private static readonly SAMPLE_RATE = VOICE_BRIDGE_CONFIG.SAMPLE_RATE; // 48000 Hz
  private static readonly CHANNELS = VOICE_BRIDGE_CONFIG.CHANNELS; // 1 (mono)
  private static readonly BIT_DEPTH = VOICE_BRIDGE_CONFIG.BIT_DEPTH; // 16 bits
  private static readonly FRAME_SIZE = VOICE_BRIDGE_CONFIG.FRAME_SIZE; // 4096 samples
  private static readonly FRAME_OVERLAP = 2048;
  private static readonly BUFFER_SIZE = 8192;
  private static readonly CHUNK_DURATION_MS = 100; // 100ms chunks matching server expectation
  
  private audioContext: AudioContext | null = null;
  private processors: AudioWorkletNode[] = [];
  private stream: MediaStream | null = null;
  private scriptProcessor: ScriptProcessorNode | null = null;
  private mediaStreamSource: MediaStreamAudioSourceNode | null = null;
  
  // PCM buffer for accumulating samples before sending
  private pcmBuffer: Int16Array = new Int16Array(0);
  private samplesPerChunk: number;
  
  // Voice session tracking
  private sessionId: string = '';
  private participantId: string = '';
  private isCapturing: boolean = false;
  private chunkSequence: number = 0;

  // Added: 2026-04-16 (PRD_PlayerLevelMicControls_v1.md § Phase B)
  // Controls audio frame gating for player_level multi-speaker mode.
  // true = frames sent normally; false = frames discarded (capture pipeline stays running,
  // so re-enabling has zero latency — no mic re-initialization needed).
  private _micEnabled: boolean = true;
  
  // Event callbacks for UI integration
  private onVoiceActivityCallback: ((isSpeaking: boolean, energyLevel: number) => void) | null = null;
  private onErrorCallback: ((error: Error, context: string) => void) | null = null;
  private onStateChangeCallback: ((state: 'idle' | 'initializing' | 'capturing' | 'error') => void) | null = null;
  
  constructor() {
    // Calculate samples needed for target chunk duration
    // 100ms at 48kHz = 4800 samples
    this.samplesPerChunk = Math.floor(AudioProcessor.SAMPLE_RATE * AudioProcessor.CHUNK_DURATION_MS / 1000);
  }
  
  /**
   * Set event callbacks for UI integration
   * 
   * @param callbacks - Object containing callback functions
   * @param callbacks.onVoiceActivity - Called when voice activity changes (for VU meter, speaking indicator)
   * @param callbacks.onError - Called when an error occurs
   * @param callbacks.onStateChange - Called when capture state changes
   */
  setCallbacks(callbacks: {
    onVoiceActivity?: (isSpeaking: boolean, energyLevel: number) => void;
    onError?: (error: Error, context: string) => void;
    onStateChange?: (state: 'idle' | 'initializing' | 'capturing' | 'error') => void;
  }): void {
    if (callbacks.onVoiceActivity) this.onVoiceActivityCallback = callbacks.onVoiceActivity;
    if (callbacks.onError) this.onErrorCallback = callbacks.onError;
    if (callbacks.onStateChange) this.onStateChangeCallback = callbacks.onStateChange;
  }

  /**
   * Set voice session identifiers (required before capturing)
   * 
   * @param sessionId - Voice session identifier (from campaign/session)
   * @param participantId - User/participant identifier
   */
  setSessionInfo(sessionId: string, participantId: string): void {
    this.sessionId = sessionId;
    this.participantId = participantId;
  }

  /**
   * Enables or disables mic audio frame sending for player_level multi-speaker mode.
   * Capture pipeline stays running — toggle is instant with no mic re-initialization delay.
   * Frames are discarded at the send layer rather than stopping getUserMedia, so re-enabling
   * resumes transmission within the next 100ms chunk cycle.
   *
   * @param enabled - true to send frames normally; false to discard frames silently
   * Added: 2026-04-16 (PRD_PlayerLevelMicControls_v1.md § Phase B)
   */
  public setMicEnabled(enabled: boolean): void {
    this._micEnabled = enabled;
    console.debug(`[AudioProcessor] Mic ${enabled ? 'enabled' : 'muted'} (player_level mode)`);
  }

  /**
   * Initialize the audio pipeline with required constraints
   * Must exactly match server-side processing pipeline (audio-pipeline.yaml)
   * 
   * Pipeline:
   * 1. getUserMedia → captures raw audio from microphone
   * 2. AudioContext → processes at 48kHz sample rate
   * 3. ScriptProcessorNode → extracts PCM samples in real-time
   * 4. PCM buffer → accumulates samples to target chunk size (100ms)
   * 5. Protobuf encode → wraps PCM in UniversalMessage
   * 6. WebSocket binary → sends to backend
   * 
   * @returns Promise<boolean> - True if initialization succeeded
   */
  async initializeMicrophone(): Promise<boolean> {
    this.onStateChangeCallback?.('initializing');
    
    // Check browser capabilities first
    const capabilities = detectVoiceCapabilities();
    if (!capabilities.webRTC || !capabilities.audioContext) {
      const error = new Error('Browser does not support required audio features (WebRTC, AudioContext)');
      this.onErrorCallback?.(error, 'initializeMicrophone');
      errorManager.reportError(
        'Browser capabilities insufficient for voice capture.',
        ErrorSeverity.CRITICAL,
        'AudioProcessor.initializeMicrophone',
        { capabilities }
      );
      this.onStateChangeCallback?.('error');
      return false;
    }
    
    try {
      // Request audio with specific constraints (from VOICE_BRIDGE_CONFIG.MEDIA_CONSTRAINTS)
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: AudioProcessor.CHANNELS,
          sampleRate: AudioProcessor.SAMPLE_RATE
        },
        video: false
      });
      
      // Create AudioContext at exact sample rate
      this.audioContext = new AudioContext({
        sampleRate: AudioProcessor.SAMPLE_RATE,
        latencyHint: 'interactive'
      });
      
      // Resume AudioContext if suspended (Chrome autoplay policy)
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      
      // Create source from microphone stream
      this.mediaStreamSource = this.audioContext.createMediaStreamSource(this.stream);
      
      // Use ScriptProcessorNode for raw PCM access
      // Note: ScriptProcessorNode is deprecated but AudioWorklet requires separate module
      // which may not be available. This provides reliable fallback.
      const bufferSize = AudioProcessor.FRAME_SIZE;
      this.scriptProcessor = this.audioContext.createScriptProcessor(bufferSize, 1, 1);
      
      // Process audio samples in real-time
      this.scriptProcessor.onaudioprocess = (event) => {
        if (!this.isCapturing) return;
        
        const inputData = event.inputBuffer.getChannelData(0);
        
        // Calculate energy level for voice activity detection
        const energy = this.calculateEnergy(inputData);
        const isSpeaking = energy > VOICE_BRIDGE_CONFIG.VOICE_DETECTION.SPEECH_THRESHOLD;
        this.onVoiceActivityCallback?.(isSpeaking, energy);
        
        // Convert Float32Array to Int16Array (PCM 16-bit)
        const pcmSamples = this.floatTo16BitPCM(inputData);
        
        // Accumulate samples
        this.accumulateSamples(pcmSamples);
      };
      
      // Connect: source → scriptProcessor → destination (required for processing)
      this.mediaStreamSource.connect(this.scriptProcessor);
      this.scriptProcessor.connect(this.audioContext.destination);
      
      this.onStateChangeCallback?.('idle');
      return true;
    } catch (error) {
      this.onStateChangeCallback?.('error');
      
      // Handle specific permission errors
      if (error instanceof DOMException) {
        if (error.name === 'NotAllowedError') {
          this.onErrorCallback?.(new Error('Microphone permission denied'), 'initializeMicrophone');
          errorManager.reportError(
            'Microphone permission denied by user.',
            ErrorSeverity.WARNING,
            'AudioProcessor.initializeMicrophone',
            error
          );
        } else if (error.name === 'NotFoundError') {
          this.onErrorCallback?.(new Error('No microphone found'), 'initializeMicrophone');
          errorManager.reportError(
            'No microphone device found.',
            ErrorSeverity.WARNING,
            'AudioProcessor.initializeMicrophone',
            error
          );
        }
      } else {
        this.onErrorCallback?.(error as Error, 'initializeMicrophone');
        errorManager.reportError(
          'Failed to initialize audio system.',
          ErrorSeverity.CRITICAL,
          'AudioProcessor.initializeMicrophone',
          error
        );
      }
      return false;
    }
  }
  
  /**
   * Calculate RMS energy level from audio samples
   * Used for voice activity detection (VAD) on client side
   * 
   * @param samples - Float32Array of audio samples
   * @returns number - RMS energy level (0.0 to 1.0)
   */
  private calculateEnergy(samples: Float32Array): number {
    let sum = 0;
    for (let i = 0; i < samples.length; i++) {
      sum += samples[i] * samples[i];
    }
    return Math.sqrt(sum / samples.length);
  }

  /**
   * Convert Float32Array audio samples to Int16Array PCM
   * Server expects 16-bit signed PCM, little-endian
   * 
   * @param float32Samples - Audio samples in Float32 format (-1.0 to 1.0)
   * @returns Int16Array - PCM samples in 16-bit format
   */
  private floatTo16BitPCM(float32Samples: Float32Array): Int16Array {
    const pcm = new Int16Array(float32Samples.length);
    for (let i = 0; i < float32Samples.length; i++) {
      // Clamp to -1.0 to 1.0 range and scale to 16-bit
      const s = Math.max(-1, Math.min(1, float32Samples[i]));
      pcm[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    return pcm;
  }

  /**
   * Accumulate PCM samples and send when chunk size is reached
   * Chunks are sent as protobuf-encoded binary messages via WebSocket
   * 
   * @param newSamples - New PCM samples to accumulate
   */
  private accumulateSamples(newSamples: Int16Array): void {
    // Combine existing buffer with new samples
    const combined = new Int16Array(this.pcmBuffer.length + newSamples.length);
    combined.set(this.pcmBuffer, 0);
    combined.set(newSamples, this.pcmBuffer.length);
    this.pcmBuffer = combined;
    
    // Send chunks when we have enough samples
    while (this.pcmBuffer.length >= this.samplesPerChunk) {
      // Extract chunk
      const chunk = this.pcmBuffer.slice(0, this.samplesPerChunk);
      this.pcmBuffer = this.pcmBuffer.slice(this.samplesPerChunk);
      
      // Send via WebSocket
      this.sendVoiceChunkViaWebSocket(chunk);
    }
  }

  /**
   * Send PCM chunk via WebSocket as protobuf-encoded binary
   * Uses UniversalMessage wrapper with VoiceDataMessage payload
   * 
   * Message structure (per websocket_protocol.md and binary_voice_messages.md):
   * - UniversalMessage wrapper
   *   - voiceData: VoiceDataMessage
   *     - sessionId: voice session identifier
   *     - participantId: user identifier
   *     - audioData: raw PCM bytes
   *     - audioFormat: "pcm"
   *     - audioConfig: sample rate, channels, bit depth
   *     - durationMs: chunk duration
   * 
   * @param pcmSamples - Int16Array of PCM samples to send
   */
  private sendVoiceChunkViaWebSocket(pcmSamples: Int16Array): void {
    // Added: 2026-04-16 (PRD_PlayerLevelMicControls_v1.md § Phase B)
    // Discard frame when mic is toggled off in player_level multi-speaker mode.
    // Capture pipeline continues running so re-enable is instant (no getUserMedia re-init).
    if (!this._micEnabled) {
      return;
    }

    try {
      // Convert Int16Array to Uint8Array (bytes)
      const audioData = new Uint8Array(pcmSamples.buffer, pcmSamples.byteOffset, pcmSamples.byteLength);
      
      // Create VoiceDataMessage
      const voiceData: mythbound.websocket.v1.IVoiceDataMessage = {
        sessionId: this.sessionId,
        participantId: this.participantId,
        audioData: audioData,
        audioFormat: 'pcm',
        audioConfig: {
          sampleRate: AudioProcessor.SAMPLE_RATE,
          channels: AudioProcessor.CHANNELS,
          bitDepth: AudioProcessor.BIT_DEPTH,
          frameSize: pcmSamples.length
        },
        durationMs: Math.floor(pcmSamples.length / AudioProcessor.SAMPLE_RATE * 1000)
      };
      
      // Wrap in UniversalMessage
      const universalMessage = mythbound.websocket.v1.UniversalMessage.create({
        voiceData: voiceData
      });
      
      // Encode to protobuf binary
      const binaryData = mythbound.websocket.v1.UniversalMessage.encode(universalMessage).finish();
      
      // Send via WebSocket service
      const sent = webSocketService.sendVoiceMessage(binaryData);
      
      if (sent) {
        this.chunkSequence++;
      } else {
        // WebSocket not ready - log but don't error (may reconnect)
        console.debug(`Voice chunk ${this.chunkSequence} not sent - WebSocket not ready`);
      }
    } catch (error) {
      this.onErrorCallback?.(error as Error, 'sendVoiceChunkViaWebSocket');
      errorManager.reportError(
        'Failed to encode/send voice chunk via WebSocket.',
        ErrorSeverity.ERROR,
        'AudioProcessor.sendVoiceChunkViaWebSocket',
        { error, chunkSequence: this.chunkSequence }
      );
    }
  }
  
  /**
   * Start audio capture and WebSocket transmission
   * Ensure initializeMicrophone() and setSessionInfo() have been called first
   * 
   * @returns Promise<boolean> - True if capture started successfully
   */
  async startCapture(): Promise<boolean> {
    // Initialize if not already done
    if (!this.audioContext || !this.stream) {
      const initialized = await this.initializeMicrophone();
      if (!initialized) return false;
    }
    
    // Validate session info is set
    if (!this.sessionId || !this.participantId) {
      const error = new Error('Session info not set. Call setSessionInfo() before startCapture()');
      this.onErrorCallback?.(error, 'startCapture');
      errorManager.reportError(
        'Cannot start capture without session info.',
        ErrorSeverity.ERROR,
        'AudioProcessor.startCapture',
        { sessionId: this.sessionId, participantId: this.participantId }
      );
      return false;
    }
    
    // Already capturing?
    if (this.isCapturing) {
      console.debug('Audio capture already active');
      return true;
    }
    
    try {
      // Resume AudioContext if suspended
      if (this.audioContext && this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      
      // Reset state
      this.pcmBuffer = new Int16Array(0);
      this.chunkSequence = 0;
      this.isCapturing = true;
      
      this.onStateChangeCallback?.('capturing');
      console.log(`Audio capture started for session ${this.sessionId}`);
      
      return true;
    } catch (error) {
      this.isCapturing = false;
      this.onStateChangeCallback?.('error');
      this.onErrorCallback?.(error as Error, 'startCapture');
      errorManager.reportError(
        'Failed to start audio capture.',
        ErrorSeverity.ERROR,
        'AudioProcessor.startCapture',
        error
      );
      return false;
    }
  }
  
  /**
   * Stop audio capture
   * Flushes any remaining buffered samples before stopping
   * 
   * @returns boolean - True if capture was stopped
   */
  stopCapture(): boolean {
    if (!this.isCapturing) {
      console.debug('Audio capture not active');
      return false;
    }
    
    try {
      this.isCapturing = false;
      
      // Flush remaining samples if any (send partial chunk)
      if (this.pcmBuffer.length > 0) {
        this.sendVoiceChunkViaWebSocket(this.pcmBuffer);
        this.pcmBuffer = new Int16Array(0);
      }
      
      this.onStateChangeCallback?.('idle');
      console.log(`Audio capture stopped. Sent ${this.chunkSequence} chunks.`);
      
      return true;
    } catch (error) {
      this.onErrorCallback?.(error as Error, 'stopCapture');
      errorManager.reportError(
        'Failed to stop audio capture.',
        ErrorSeverity.ERROR,
        'AudioProcessor.stopCapture',
        error
      );
      return false;
    }
  }

  /**
   * Check if currently capturing audio
   * 
   * @returns boolean - True if capture is active
   */
  isActive(): boolean {
    return this.isCapturing;
  }

  /**
   * Get current capture statistics
   * 
   * @returns Object with capture stats
   */
  getStats(): {
    isCapturing: boolean;
    chunksSent: number;
    bufferedSamples: number;
    sessionId: string;
    participantId: string;
    sampleRate: number;
    channels: number;
    bitDepth: number;
  } {
    return {
      isCapturing: this.isCapturing,
      chunksSent: this.chunkSequence,
      bufferedSamples: this.pcmBuffer.length,
      sessionId: this.sessionId,
      participantId: this.participantId,
      sampleRate: AudioProcessor.SAMPLE_RATE,
      channels: AudioProcessor.CHANNELS,
      bitDepth: AudioProcessor.BIT_DEPTH
    };
  }
  
  // ========================================================================
  // TEST UTILITIES (PRD 5)
  // ========================================================================

  /**
   * Send test tone for round-trip verification
   * Used by integration tests and !voice_e2e_test command
   * 
   * Generates a sine wave at the specified frequency and sends it through
   * the normal voice pipeline. Useful for:
   * - Verifying audio format matches audio-pipeline.yaml (48kHz/16-bit/mono)
   * - Measuring round-trip latency
   * - Testing voice_echo endpoint (DEV/TEST only)
   * 
   * @param frequency - Tone frequency in Hz (default: 440 = A4 note)
   * @param durationMs - Tone duration in milliseconds (default: 1000)
   * @returns Object with send status and bytes sent
   */
  async sendTestTone(
    frequency: number = 440,
    durationMs: number = 1000
  ): Promise<{ sent: boolean; bytesSent: number; error?: string }> {
    // Validate session info is set
    if (!this.sessionId || !this.participantId) {
      return {
        sent: false,
        bytesSent: 0,
        error: 'Session info not set. Call setSessionInfo() before sendTestTone()'
      };
    }

    try {
      // Generate sine wave samples
      const samples = this.generateSineWave(frequency, durationMs);
      
      // Convert to 16-bit PCM
      const pcmData = this.floatTo16BitPCM(samples);
      
      // Send via normal pipeline
      this.sendVoiceChunkViaWebSocket(pcmData);
      
      console.log(
        `Test tone sent: ${frequency}Hz for ${durationMs}ms ` +
        `(${pcmData.byteLength} bytes, ${pcmData.length} samples)`
      );
      
      return {
        sent: true,
        bytesSent: pcmData.byteLength
      };
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      errorManager.reportError(
        'Failed to send test tone.',
        ErrorSeverity.ERROR,
        'AudioProcessor.sendTestTone',
        { error, frequency, durationMs }
      );
      return {
        sent: false,
        bytesSent: 0,
        error: errorMsg
      };
    }
  }

  /**
   * Generate a sine wave at the specified frequency
   * Used for test tone generation
   * 
   * @param frequency - Frequency in Hz
   * @param durationMs - Duration in milliseconds
   * @returns Float32Array of audio samples (range -1.0 to 1.0)
   */
  private generateSineWave(frequency: number, durationMs: number): Float32Array {
    const sampleCount = Math.floor(AudioProcessor.SAMPLE_RATE * durationMs / 1000);
    const samples = new Float32Array(sampleCount);
    const angularFrequency = 2 * Math.PI * frequency / AudioProcessor.SAMPLE_RATE;
    
    for (let i = 0; i < sampleCount; i++) {
      // Generate sine wave with slight amplitude ramp to avoid clicks
      let amplitude = 0.8; // Slightly below max to avoid clipping
      
      // Fade in first 10ms
      const fadeInSamples = Math.floor(AudioProcessor.SAMPLE_RATE * 0.01);
      if (i < fadeInSamples) {
        amplitude *= i / fadeInSamples;
      }
      
      // Fade out last 10ms
      const fadeOutStart = sampleCount - fadeInSamples;
      if (i > fadeOutStart) {
        amplitude *= (sampleCount - i) / fadeInSamples;
      }
      
      samples[i] = amplitude * Math.sin(angularFrequency * i);
    }
    
    return samples;
  }

  /**
   * Generate white noise for testing
   * Alternative test signal that covers all frequencies
   * 
   * @param durationMs - Duration in milliseconds
   * @param amplitude - Peak amplitude (0.0 to 1.0, default: 0.5)
   * @returns Float32Array of audio samples
   */
  generateWhiteNoise(durationMs: number, amplitude: number = 0.5): Float32Array {
    const sampleCount = Math.floor(AudioProcessor.SAMPLE_RATE * durationMs / 1000);
    const samples = new Float32Array(sampleCount);
    
    for (let i = 0; i < sampleCount; i++) {
      // Random value between -amplitude and +amplitude
      samples[i] = (Math.random() * 2 - 1) * amplitude;
    }
    
    return samples;
  }

  /**
   * Send custom audio samples for testing
   * Allows sending arbitrary PCM data through the pipeline
   * 
   * @param samples - Float32Array of audio samples (range -1.0 to 1.0)
   * @returns Object with send status and bytes sent
   */
  async sendTestSamples(
    samples: Float32Array
  ): Promise<{ sent: boolean; bytesSent: number; error?: string }> {
    if (!this.sessionId || !this.participantId) {
      return {
        sent: false,
        bytesSent: 0,
        error: 'Session info not set. Call setSessionInfo() before sendTestSamples()'
      };
    }

    try {
      const pcmData = this.floatTo16BitPCM(samples);
      this.sendVoiceChunkViaWebSocket(pcmData);
      
      return {
        sent: true,
        bytesSent: pcmData.byteLength
      };
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      return {
        sent: false,
        bytesSent: 0,
        error: errorMsg
      };
    }
  }

  // ========================================================================
  // RESOURCE CLEANUP
  // ========================================================================

  /**
   * Clean up all resources
   * Call this when the voice session ends or component unmounts
   */
  dispose(): void {
    this.stopCapture();
    
    // Disconnect script processor
    if (this.scriptProcessor) {
      try {
        this.scriptProcessor.disconnect();
        this.scriptProcessor.onaudioprocess = null;
        this.scriptProcessor = null;
      } catch (e) {
        errorManager.reportError(
          'Error disconnecting script processor.',
          ErrorSeverity.WARNING,
          'AudioProcessor.dispose',
          e
        );
      }
    }
    
    // Disconnect media stream source
    if (this.mediaStreamSource) {
      try {
        this.mediaStreamSource.disconnect();
        this.mediaStreamSource = null;
      } catch (e) {
        errorManager.reportError(
          'Error disconnecting media stream source.',
          ErrorSeverity.WARNING,
          'AudioProcessor.dispose',
          e
        );
      }
    }
    
    // Disconnect and clean up any worklet processors
    this.processors.forEach(processor => {
      try {
        processor.disconnect();
      } catch (e) {
        errorManager.reportError(
          'Error disconnecting processor node.',
          ErrorSeverity.WARNING,
          'AudioProcessor.dispose',
          e
        );
      }
    });
    this.processors = [];
    
    // Stop and release media stream tracks
    if (this.stream) {
      this.stream.getTracks().forEach(track => {
        track.stop();
      });
      this.stream = null;
    }
    
    // Close audio context
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(e => {
        errorManager.reportError(
          'Error closing AudioContext.',
          ErrorSeverity.WARNING,
          'AudioProcessor.dispose',
          e
        );
      });
      this.audioContext = null;
    }
    
    // Clear buffers and state
    this.pcmBuffer = new Int16Array(0);
    this.chunkSequence = 0;
    this.sessionId = '';
    this.participantId = '';
    
    // Clear callbacks
    this.onVoiceActivityCallback = null;
    this.onErrorCallback = null;
    const onStateChangeCallback = this.onStateChangeCallback;
    this.onStateChangeCallback = null;
    
    onStateChangeCallback?.('idle');
    console.log('AudioProcessor disposed');
  }
}

/**
 * Singleton instance of the AudioProcessor
 * Use this for single voice session per browser tab
 */
export const audioProcessor = new AudioProcessor();

/**
 * Factory function to create new AudioProcessor instances
 * Use this if you need multiple independent processors
 * 
 * @returns AudioProcessor - New AudioProcessor instance
 */
export function createAudioProcessor(): AudioProcessor {
  return new AudioProcessor();
}
